import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../../axios";
import { userActions } from "../../../_actions";
import { 
  FaUser, FaBell, FaPalette, FaShieldAlt, FaSave, FaCheck, 
  FaInfoCircle, FaAdjust, FaServer, FaCog, FaBrain, FaEnvelope, 
  FaExchangeAlt, FaLock, FaDatabase, FaHistory, FaCloudUploadAlt, 
  FaSyncAlt, FaEye, FaEyeSlash, FaWrench
} from "react-icons/fa";
import { Loading } from "../../Other/Loading";

export default function SettingsPage() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.users);
  const isAdmin = user?.role === 1 || user?.role == "1";

  const [activeTab, setActiveTab] = useState(isAdmin ? "system_portal" : "general");
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [purgingCache, setPurgingCache] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  
  // Show/hide API keys
  const [showKeys, setShowKeys] = useState({});
  const toggleShowKey = (key) => {
    setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Form states (Personal)
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    language: localStorage.getItem("language") || "en"
  });

  const [notifications, setNotifications] = useState({
    emailAnnouncements: true,
    emailReminders: true,
    emailQuizzes: false,
    pushWebinars: true,
    pushAttendance: true
  });

  const [security, setSecurity] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    twoFactor: false
  });

  // Admin settings state
  const [adminSettings, setAdminSettings] = useState({
    mode: "test",
    general: {
      portalName: "Yuvasakthi Academy AI",
      supportEmail: "support@yslearn.in",
      supportPhone: "+1 (555) 123-4567",
      allowRegistration: true,
      maintenanceMode: false,
      copyrightText: "Copyright © 2026 yslearn.in"
    },
    payment: {
      stripeEnabled: true,
      stripeMode: "test",
      stripePublicKey: "",
      stripeSecretKey: "",
      paypalEnabled: false,
      paypalMode: "sandbox",
      paypalClientId: "",
      razorpayEnabled: false,
      razorpayKeyId: ""
    },
    ai: {
      openaiKey: "",
      geminiKey: "",
      anthropicKey: "",
      defaultModel: "gpt-4o",
      temperature: 0.7,
      maxTokens: 2048,
      defaultSystemPrompt: "You are Yuvasakthi Academy AI, a helpful virtual assistant."
    },
    smtp: {
      host: "smtp.mailtrap.io",
      port: 587,
      user: "",
      password: "",
      secure: false,
      senderName: "Yuvasakthi Academy AI",
      senderEmail: "noreply@yslearn.in"
    },
    security: {
      enforceMfa: false,
      maxLoginAttempts: 5,
      sessionTimeout: 60
    }
  });

  const [diagnostics, setDiagnostics] = useState(null);

  // Theme states
  const [currentTheme, setCurrentTheme] = useState(localStorage.getItem("smik-theme") || "light");

  const themes = [
    {
      id: "light",
      name: "Light Slate",
      description: "Default clean slate layout with modern slate and blue highlights.",
      bgClass: "bg-slate-50",
      cardBgClass: "bg-white",
      textClass: "text-slate-800",
      borderClass: "border-slate-200",
      colorPreview: "from-blue-500 to-indigo-600"
    },
    {
      id: "dark",
      name: "Dark Charcoal",
      description: "Premium rich dark carbon interface designed to minimize eye strain.",
      bgClass: "bg-[#0b0f19]",
      cardBgClass: "bg-[#161d31]",
      textClass: "text-slate-100",
      borderClass: "border-slate-800",
      colorPreview: "from-slate-800 to-slate-900"
    },
    {
      id: "indigo",
      name: "Midnight Sapphire",
      description: "Immersive deep royal blue and indigo gradient theme for focused learning.",
      bgClass: "bg-[#050b14]",
      cardBgClass: "bg-[#0b1528]",
      textClass: "text-indigo-100",
      borderClass: "border-indigo-950",
      colorPreview: "from-indigo-600 to-violet-700"
    },
    {
      id: "emerald",
      name: "Emerald Forest",
      description: "A calming natural setup utilizing organic dark emerald green colors.",
      bgClass: "bg-[#021d17]",
      cardBgClass: "bg-[#042f26]",
      textClass: "text-emerald-50",
      borderClass: "border-emerald-950/60",
      colorPreview: "from-emerald-500 to-teal-600"
    }
  ];

  // Apply theme dynamically
  const applyTheme = (themeId) => {
    setCurrentTheme(themeId);
    localStorage.setItem("smik-theme", themeId);
    
    if (themeId === "light") {
      document.documentElement.classList.remove("dark");
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    } else {
      document.documentElement.classList.add("dark");
    }

    if (themeId === "dark") {
      document.documentElement.style.setProperty("--theme-bg", "#0b0f19");
      document.documentElement.style.setProperty("--theme-card-bg", "#161d31");
      document.documentElement.style.setProperty("--theme-text", "#f8fafc");
      document.documentElement.style.setProperty("--theme-border", "#1e293b");
    } else if (themeId === "indigo") {
      document.documentElement.style.setProperty("--theme-bg", "#050b14");
      document.documentElement.style.setProperty("--theme-card-bg", "#0b1528");
      document.documentElement.style.setProperty("--theme-text", "#e0e7ff");
      document.documentElement.style.setProperty("--theme-border", "#1e1b4b");
    } else if (themeId === "emerald") {
      document.documentElement.style.setProperty("--theme-bg", "#021d17");
      document.documentElement.style.setProperty("--theme-card-bg", "#042f26");
      document.documentElement.style.setProperty("--theme-text", "#ecfdf5");
      document.documentElement.style.setProperty("--theme-border", "#064e3b");
    } else {
      document.documentElement.style.setProperty("--theme-bg", "#f8fafc");
      document.documentElement.style.setProperty("--theme-card-bg", "#ffffff");
      document.documentElement.style.setProperty("--theme-text", "#0f172a");
      document.documentElement.style.setProperty("--theme-border", "#e2e8f0");
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem("smik-theme") || "light";
    applyTheme(savedTheme);
  }, []);

  // Fetch admin configs if admin
  useEffect(() => {
    if (user && isAdmin) {
      fetchAdminSettings();
    }
  }, [user]);

  const fetchAdminSettings = async () => {
    setLoadingConfig(true);
    try {
      const response = await axiosInstance.get("admin/system-settings");
      if (response.data && !response.data.error) {
        setAdminSettings(response.data.settings);
        setDiagnostics(response.data.diagnostics);
      }
    } catch (err) {
      console.error("Error fetching admin settings:", err);
    } finally {
      setLoadingConfig(false);
    }
  };

  // Submit Profile Details (Real PUT)
  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        language: profileData.language
      };
      await axiosInstance.put("user/profile", payload);
      setSuccessMsg("Personal profile updated successfully!");
      dispatch(userActions.getUserInfo());
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      console.error(err);
      alert("Failed to update profile details: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    setTimeout(() => {
      setSuccessMsg("Notification preferences saved locally!");
      setSaving(false);
      setTimeout(() => setSuccessMsg(""), 3000);
    }, 1000);
  };

  const handleSecuritySubmit = (e) => {
    e.preventDefault();
    if (security.newPassword !== security.confirmPassword) {
      alert("New passwords do not match!");
      return;
    }
    setSaving(true);
    setTimeout(() => {
      setSuccessMsg("Security settings updated successfully!");
      setSecurity({ currentPassword: "", newPassword: "", confirmPassword: "", twoFactor: security.twoFactor });
      setSaving(false);
      setTimeout(() => setSuccessMsg(""), 3000);
    }, 1000);
  };

  // Submit Global Admin Settings (POST)
  const handleAdminSettingsSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axiosInstance.post("admin/system-settings", adminSettings);
      if (response.data && !response.data.error) {
        setSuccessMsg("Global system configurations saved successfully!");
        fetchAdminSettings(); // Refresh configs and diagnostics
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error("Error saving admin settings:", err);
      alert("Failed to save global configurations: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Diagnostic actions
  const handlePurgeCache = () => {
    setPurgingCache(true);
    setTimeout(() => {
      setPurgingCache(false);
      setSuccessMsg("System application cache purged successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    }, 1500);
  };

  const handleExportBackup = () => {
    setBackingUp(true);
    setTimeout(() => {
      setBackingUp(false);
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(adminSettings, null, 2));
      const downloadAnchor = document.createElement('a');
      downloadAnchor.setAttribute("href", dataStr);
      downloadAnchor.setAttribute("download", `smik_learn_config_backup_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
      setSuccessMsg("System settings backup downloaded successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    }, 1000);
  };

  // Dynamic Tabs lists
  const adminTabs = [
    { id: "system_portal", label: "Portal Configurations", icon: <FaWrench />, section: "Global Settings" },
    { id: "ai_engines", label: "AI Engines (LLMs)", icon: <FaBrain />, section: "Global Settings" },
    { id: "smtp_server", label: "SMTP Mail Setup", icon: <FaEnvelope />, section: "Global Settings" },
    { id: "payment_gateways", label: "Payment Processors", icon: <FaExchangeAlt />, section: "Global Settings" },
    { id: "security_policies", label: "Security & Policies", icon: <FaLock />, section: "Global Settings" },
    { id: "diagnostics", label: "System Diagnostics", icon: <FaServer />, section: "Global Settings" },
    { id: "general", label: "Personal Profile", icon: <FaUser />, section: "Personal Details" },
    { id: "themes", label: "Aesthetic Themes", icon: <FaPalette />, section: "Personal Details" }
  ];

  const userTabs = [
    { id: "general", label: "Personal Details", icon: <FaUser /> },
    { id: "notifications", label: "Notifications", icon: <FaBell /> },
    { id: "security", label: "Account Security", icon: <FaShieldAlt /> },
    { id: "themes", label: "Theme Options", icon: <FaPalette /> }
  ];

  const activeTabsList = isAdmin ? adminTabs : userTabs;

  return (
    <div className="container-wrapper p-6 bg-slate-50/50 min-h-screen dark:bg-[#0b0f19] dark:text-slate-100">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 pb-5 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 dark:text-white flex items-center gap-3 tracking-tight">
            <span className="p-3 bg-gradient-to-tr from-slate-700 to-slate-900 dark:from-indigo-600 dark:to-violet-700 rounded-2xl text-white shadow-lg">
              <FaAdjust size={20} />
            </span>
            {isAdmin ? "Administrative Command Center" : "System Settings"}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium">
            {isAdmin 
              ? "Oversee global AI adapters, control API configurations, configure payment hooks, monitor server performance and manage profiles."
              : "Customize your dashboard options, update security details, and adjust active screen themes."
            }
          </p>
        </div>
        
        {isAdmin && diagnostics && (
          <div className="mt-4 md:mt-0 flex flex-wrap gap-2.5 items-center bg-white dark:bg-[#161d31] p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-md shadow-slate-100/50 dark:shadow-none">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
              <span className="text-[11px] font-bold uppercase text-slate-400 dark:text-slate-500">DB Status:</span>
              <span className={`text-xs font-extrabold ${diagnostics.dbStatus.startsWith("Connected") ? "text-emerald-500" : "text-rose-500"}`}>
                {diagnostics.dbStatus.split(":")[0]}
              </span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-200 dark:bg-slate-800"></div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase text-slate-400 dark:text-slate-500">Server Mode:</span>
              <span className={`text-xs font-extrabold px-2 py-0.5 rounded-full uppercase border ${
                adminSettings.mode === "live" 
                  ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500" 
                  : "bg-amber-500/10 border-amber-500/20 text-amber-500"
              }`}>
                {adminSettings.mode}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Main Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        
        {/* Left Column Sidebar Tabs */}
        <div className="lg:col-span-1 flex flex-col gap-4">
          {isAdmin ? (
            // Grouped admin navigation
            <div className="flex flex-col gap-4">
              {["Global Settings", "Personal Details"].map((sectionName) => {
                const sectionTabs = activeTabsList.filter(t => t.section === sectionName);
                return (
                  <div key={sectionName} className="flex flex-col gap-1.5">
                    <span className="text-[10px] font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-3">
                      {sectionName}
                    </span>
                    {sectionTabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-xs font-extrabold transition-all border text-left ${
                          activeTab === tab.id
                            ? "bg-slate-800 text-white border-slate-800 shadow-md dark:bg-indigo-600 dark:border-indigo-600 dark:shadow-none"
                            : "bg-white text-slate-600 border-slate-200/80 hover:bg-slate-50 hover:text-slate-800 dark:bg-[#161d31] dark:text-slate-300 dark:border-slate-800/80 dark:hover:bg-[#1f2945] dark:hover:text-white"
                        }`}
                      >
                        <span className="text-base shrink-0">{tab.icon}</span>
                        {tab.label}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : (
            // Regular user tabs
            <div className="flex flex-col gap-2">
              {activeTabsList.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-5 py-4 rounded-2xl text-sm font-extrabold transition-all border text-left ${
                    activeTab === tab.id
                      ? "bg-slate-800 text-white border-slate-800 shadow-md"
                      : "bg-white text-slate-600 border-slate-200/80 hover:bg-slate-50 hover:text-slate-800"
                  }`}
                >
                  <span className="text-base shrink-0">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Column Custom Content Panels */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-[#161d31] rounded-3xl border border-slate-200/80 dark:border-slate-800/80 p-6 md:p-8 shadow-xl shadow-slate-100/50 dark:shadow-none relative">
            
            {loadingConfig && (
              <div className="absolute inset-0 bg-white/70 dark:bg-[#161d31]/70 backdrop-blur-sm rounded-3xl flex items-center justify-center z-50">
                <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400 font-semibold">
                  <Loading size={32} />
                  <span>Loading configuration from server...</span>
                </div>
              </div>
            )}

            {successMsg && (
              <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-800 dark:text-emerald-400 rounded-2xl text-sm font-bold flex items-center gap-2.5 animate-in fade-in slide-in-from-top-3 z-10">
                <FaCheck className="bg-emerald-500 text-white p-1 rounded-full w-5 h-5 shrink-0" />
                {successMsg}
              </div>
            )}

            {/* TAB 1: PERSONAL DETAILS (Available to all) */}
            {activeTab === "general" && (
              <form onSubmit={handleGeneralSubmit}>
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
                  <span className="w-2.5 h-6 bg-slate-800 dark:bg-indigo-500 rounded-full"></span>
                  Personal Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">First Name</label>
                    <input
                      type="text"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Last Name</label>
                    <input
                      type="text"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Email Address</label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Phone Number</label>
                    <input
                      type="text"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-extrabold px-6 py-3 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loading size={12} /> : <FaSave />}
                    Save Profile Settings
                  </button>
                </div>
              </form>
            )}

            {/* TAB 2: NOTIFICATIONS (Standard Users) */}
            {activeTab === "notifications" && !isAdmin && (
              <form onSubmit={handleNotificationSubmit}>
                <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                  <span className="w-2.5 h-6 bg-slate-800 rounded-full"></span>
                  Notification Toggles
                </h3>

                <div className="flex flex-col gap-5 mb-8">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <p className="text-sm font-bold text-slate-800">Email announcements</p>
                      <p className="text-xs text-slate-400 font-medium">Receive announcements regarding course updates.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNotifications({ ...notifications, emailAnnouncements: !notifications.emailAnnouncements })}
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        notifications.emailAnnouncements ? "bg-emerald-500" : "bg-slate-200"
                      }`}
                    >
                      <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                        notifications.emailAnnouncements ? "right-1" : "left-1"
                      }`}></span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <p className="text-sm font-bold text-slate-800">Email class reminders</p>
                      <p className="text-xs text-slate-400 font-medium">Get notifications a day before scheduled webinars.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNotifications({ ...notifications, emailReminders: !notifications.emailReminders })}
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        notifications.emailReminders ? "bg-emerald-500" : "bg-slate-200"
                      }`}
                    >
                      <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                        notifications.emailReminders ? "right-1" : "left-1"
                      }`}></span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <p className="text-sm font-bold text-slate-800">Email quiz reports</p>
                      <p className="text-xs text-slate-400 font-medium">Receive copy of quiz results upon score compilation.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNotifications({ ...notifications, emailQuizzes: !notifications.emailQuizzes })}
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        notifications.emailQuizzes ? "bg-emerald-500" : "bg-slate-200"
                      }`}
                    >
                      <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                        notifications.emailQuizzes ? "right-1" : "left-1"
                      }`}></span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <p className="text-sm font-bold text-slate-800">Browser push notifications</p>
                      <p className="text-xs text-slate-400 font-medium">Get alerts when interactive sessions go live.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNotifications({ ...notifications, pushWebinars: !notifications.pushWebinars })}
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        notifications.pushWebinars ? "bg-emerald-500" : "bg-slate-200"
                      }`}
                    >
                      <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                        notifications.pushWebinars ? "right-1" : "left-1"
                      }`}></span>
                    </button>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-extrabold px-6 py-3 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loading size={12} /> : <FaSave />}
                    Save Notification Toggles
                  </button>
                </div>
              </form>
            )}

            {/* TAB 3: ACCOUNT SECURITY (Standard Users) */}
            {activeTab === "security" && !isAdmin && (
              <form onSubmit={handleSecuritySubmit}>
                <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                  <span className="w-2.5 h-6 bg-slate-800 rounded-full"></span>
                  Change Password
                </h3>

                <div className="flex flex-col gap-5 mb-6">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Current Password</label>
                    <input
                      type="password"
                      value={security.currentPassword}
                      onChange={(e) => setSecurity({ ...security, currentPassword: e.target.value })}
                      className="w-full border border-slate-200 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 bg-slate-50/50 focus:outline-none focus:border-slate-500 focus:bg-white transition-all"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">New Password</label>
                      <input
                        type="password"
                        value={security.newPassword}
                        onChange={(e) => setSecurity({ ...security, newPassword: e.target.value })}
                        className="w-full border border-slate-200 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 bg-slate-50/50 focus:outline-none focus:border-slate-500 focus:bg-white transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Confirm New Password</label>
                      <input
                        type="password"
                        value={security.confirmPassword}
                        onChange={(e) => setSecurity({ ...security, confirmPassword: e.target.value })}
                        className="w-full border border-slate-200 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 bg-slate-50/50 focus:outline-none focus:border-slate-500 focus:bg-white transition-all"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-slate-100 pt-6 mb-8">
                  <div>
                    <p className="text-sm font-bold text-slate-800">Two-Factor Authentication</p>
                    <p className="text-xs text-slate-400 font-medium">Protect your portal account with second-layer security.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSecurity({ ...security, twoFactor: !security.twoFactor })}
                    className={`w-11 h-6 rounded-full transition-all relative ${
                      security.twoFactor ? "bg-emerald-500" : "bg-slate-200"
                    }`}
                  >
                    <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                      security.twoFactor ? "right-1" : "left-1"
                    }`}></span>
                  </button>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-extrabold px-6 py-3 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loading size={12} /> : <FaSave />}
                    Update Password
                  </button>
                </div>
              </form>
            )}

            {/* ADMIN TAB 1: PORTAL CONFIGURATIONS */}
            {activeTab === "system_portal" && isAdmin && (
              <form onSubmit={handleAdminSettingsSubmit}>
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
                  <span className="w-2.5 h-6 bg-slate-800 dark:bg-indigo-500 rounded-full"></span>
                  Portal Setup & Brand Info
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Portal Name</label>
                    <input
                      type="text"
                      value={adminSettings.general.portalName}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        general: { ...adminSettings.general, portalName: e.target.value }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">System Environment Mode</label>
                    <select
                      value={adminSettings.mode}
                      onChange={(e) => setAdminSettings({ ...adminSettings, mode: e.target.value })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                    >
                      <option value="test">Test Mode (Bypasses Checkout, sandbox values enabled)</option>
                      <option value="live">Live Production Mode</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Support Contact Email</label>
                    <input
                      type="email"
                      value={adminSettings.general.supportEmail}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        general: { ...adminSettings.general, supportEmail: e.target.value }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Support Phone</label>
                    <input
                      type="text"
                      value={adminSettings.general.supportPhone}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        general: { ...adminSettings.general, supportPhone: e.target.value }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Copyright Text Template</label>
                  <input
                    type="text"
                    value={adminSettings.general.copyrightText}
                    onChange={(e) => setAdminSettings({
                      ...adminSettings,
                      general: { ...adminSettings.general, copyrightText: e.target.value }
                    })}
                    className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 bg-slate-50 dark:bg-[#0b0f19] rounded-2xl border border-slate-200/60 dark:border-slate-800/60 mb-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-800 dark:text-slate-100">User Registrations</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">Control if new students can sign up.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAdminSettings({
                        ...adminSettings,
                        general: { ...adminSettings.general, allowRegistration: !adminSettings.general.allowRegistration }
                      })}
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        adminSettings.general.allowRegistration ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-800"
                      }`}
                    >
                      <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                        adminSettings.general.allowRegistration ? "right-1" : "left-1"
                      }`}></span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-800 dark:text-slate-100">System Maintenance Mode</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">Put system offline to all non-admins.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAdminSettings({
                        ...adminSettings,
                        general: { ...adminSettings.general, maintenanceMode: !adminSettings.general.maintenanceMode }
                      })}
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        adminSettings.general.maintenanceMode ? "bg-amber-500" : "bg-slate-300 dark:bg-slate-800"
                      }`}
                    >
                      <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                        adminSettings.general.maintenanceMode ? "right-1" : "left-1"
                      }`}></span>
                    </button>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-extrabold px-6 py-3 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loading size={12} /> : <FaSave />}
                    Save System Configurations
                  </button>
                </div>
              </form>
            )}

            {/* ADMIN TAB 2: AI INTEGRATIONS */}
            {activeTab === "ai_engines" && isAdmin && (
              <form onSubmit={handleAdminSettingsSubmit}>
                <div className="flex justify-between items-center mb-6 border-b border-slate-150 dark:border-slate-800 pb-3">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                    <span className="w-2.5 h-6 bg-slate-800 dark:bg-indigo-500 rounded-full"></span>
                    AI Prompt & LLM Adapters
                  </h3>
                  <span className="flex items-center gap-1 text-[11px] font-extrabold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-100 dark:border-indigo-500/20 uppercase tracking-wider">
                    <FaBrain /> Active Engine
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-5 mb-6">
                  {/* OpenAI Key */}
                  <div className="relative">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">OpenAI API Key</label>
                    <div className="relative">
                      <input
                        type={showKeys["openai"] ? "text" : "password"}
                        value={adminSettings.ai.openaiKey}
                        onChange={(e) => setAdminSettings({
                          ...adminSettings,
                          ai: { ...adminSettings.ai, openaiKey: e.target.value }
                        })}
                        placeholder="sk-proj-..........................................."
                        className="w-full border border-slate-200 dark:border-slate-800 pl-4 pr-12 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey("openai")}
                        className="absolute right-4 top-3.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-350"
                      >
                        {showKeys["openai"] ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  {/* Gemini Key */}
                  <div className="relative">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Google Gemini API Key</label>
                    <div className="relative">
                      <input
                        type={showKeys["gemini"] ? "text" : "password"}
                        value={adminSettings.ai.geminiKey}
                        onChange={(e) => setAdminSettings({
                          ...adminSettings,
                          ai: { ...adminSettings.ai, geminiKey: e.target.value }
                        })}
                        placeholder="AIzaSy..........................................."
                        className="w-full border border-slate-200 dark:border-slate-800 pl-4 pr-12 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey("gemini")}
                        className="absolute right-4 top-3.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-350"
                      >
                        {showKeys["gemini"] ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  {/* Anthropic Key */}
                  <div className="relative">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Anthropic Claude API Key</label>
                    <div className="relative">
                      <input
                        type={showKeys["anthropic"] ? "text" : "password"}
                        value={adminSettings.ai.anthropicKey}
                        onChange={(e) => setAdminSettings({
                          ...adminSettings,
                          ai: { ...adminSettings.ai, anthropicKey: e.target.value }
                        })}
                        placeholder="sk-ant-..........................................."
                        className="w-full border border-slate-200 dark:border-slate-800 pl-4 pr-12 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey("anthropic")}
                        className="absolute right-4 top-3.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-350"
                      >
                        {showKeys["anthropic"] ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Default Model</label>
                    <select
                      value={adminSettings.ai.defaultModel}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        ai: { ...adminSettings.ai, defaultModel: e.target.value }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                    >
                      <option value="gpt-4o">OpenAI GPT-4o (Default)</option>
                      <option value="gpt-4-turbo">OpenAI GPT-4 Turbo</option>
                      <option value="gemini-1.5-pro">Google Gemini 1.5 Pro</option>
                      <option value="gemini-1.5-flash">Google Gemini 1.5 Flash</option>
                      <option value="claude-3-5-sonnet">Anthropic Claude 3.5 Sonnet</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">
                      Temperature ({adminSettings.ai.temperature})
                    </label>
                    <div className="pt-2">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={adminSettings.ai.temperature}
                        onChange={(e) => setAdminSettings({
                          ...adminSettings,
                          ai: { ...adminSettings.ai, temperature: parseFloat(e.target.value) }
                        })}
                        className="w-full accent-indigo-600 cursor-pointer h-2 bg-slate-200 dark:bg-[#0b0f19] rounded-lg appearance-none"
                      />
                      <div className="flex justify-between text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mt-1">
                        <span>Precise (0.0)</span>
                        <span>Balanced (0.5)</span>
                        <span>Creative (1.0)</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Max Tokens Limit</label>
                    <input
                      type="number"
                      value={adminSettings.ai.maxTokens}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        ai: { ...adminSettings.ai, maxTokens: parseInt(e.target.value) || 2048 }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="mb-8">
                  <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Default System Prompt</label>
                  <textarea
                    rows={4}
                    value={adminSettings.ai.defaultSystemPrompt}
                    onChange={(e) => setAdminSettings({
                      ...adminSettings,
                      ai: { ...adminSettings.ai, defaultSystemPrompt: e.target.value }
                    })}
                    className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all leading-relaxed"
                  />
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-extrabold px-6 py-3 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loading size={12} /> : <FaSave />}
                    Save AI Settings
                  </button>
                </div>
              </form>
            )}

            {/* ADMIN TAB 3: SMTP MAIL SERVER SETUP */}
            {activeTab === "smtp_server" && isAdmin && (
              <form onSubmit={handleAdminSettingsSubmit}>
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
                  <span className="w-2.5 h-6 bg-slate-800 dark:bg-indigo-500 rounded-full"></span>
                  SMTP Outbound Mail Settings
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                  <div className="md:col-span-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">SMTP Host Server</label>
                    <input
                      type="text"
                      value={adminSettings.smtp.host}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        smtp: { ...adminSettings.smtp, host: e.target.value }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">SMTP Port</label>
                    <input
                      type="number"
                      value={adminSettings.smtp.port}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        smtp: { ...adminSettings.smtp, port: parseInt(e.target.value) || 587 }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">SMTP Username</label>
                    <input
                      type="text"
                      value={adminSettings.smtp.user}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        smtp: { ...adminSettings.smtp, user: e.target.value }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">SMTP Password</label>
                    <input
                      type="password"
                      value={adminSettings.smtp.password}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        smtp: { ...adminSettings.smtp, password: e.target.value }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Sender Name</label>
                    <input
                      type="text"
                      value={adminSettings.smtp.senderName}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        smtp: { ...adminSettings.smtp, senderName: e.target.value }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Sender Email</label>
                    <input
                      type="email"
                      value={adminSettings.smtp.senderEmail}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        smtp: { ...adminSettings.smtp, senderEmail: e.target.value }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-[#0b0f19] rounded-2xl mb-8 border border-slate-200/60 dark:border-slate-800/60">
                  <button
                    type="button"
                    onClick={() => setAdminSettings({
                      ...adminSettings,
                      smtp: { ...adminSettings.smtp, secure: !adminSettings.smtp.secure }
                    })}
                    className={`w-11 h-6 rounded-full transition-all relative shrink-0 ${
                      adminSettings.smtp.secure ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-800"
                    }`}
                  >
                    <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                      adminSettings.smtp.secure ? "right-1" : "left-1"
                    }`}></span>
                  </button>
                  <div>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100">Enforce Secure Connection (SSL/TLS)</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">Use encrypted tunnel on port 465 (or TLS starttls upgrade on port 587).</p>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-extrabold px-6 py-3 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loading size={12} /> : <FaSave />}
                    Save Mail Server Config
                  </button>
                </div>
              </form>
            )}

            {/* ADMIN TAB 4: PAYMENT PROCESSORS */}
            {activeTab === "payment_gateways" && isAdmin && (
              <form onSubmit={handleAdminSettingsSubmit}>
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
                  <span className="w-2.5 h-6 bg-slate-800 dark:bg-indigo-500 rounded-full"></span>
                  Checkout Integration & Payment Gateways
                </h3>

                {/* Stripe Config */}
                <div className="p-5 bg-slate-50/50 dark:bg-[#0b0f19]/30 border border-slate-200 dark:border-slate-800/80 rounded-2xl mb-6">
                  <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-slate-800/60 pb-3 mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center text-white font-black text-sm">S</div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Stripe Payment Gateway</h4>
                        <p className="text-xs text-slate-400 dark:text-slate-500 font-semibold">Processes credit cards, Apple Pay, and digital wallets.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAdminSettings({
                        ...adminSettings,
                        payment: { ...adminSettings.payment, stripeEnabled: !adminSettings.payment.stripeEnabled }
                      })}
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        adminSettings.payment.stripeEnabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-800"
                      }`}
                    >
                      <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                        adminSettings.payment.stripeEnabled ? "right-1" : "left-1"
                      }`}></span>
                    </button>
                  </div>

                  {adminSettings.payment.stripeEnabled && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-1.5">Stripe Gateway Mode</label>
                        <select
                          value={adminSettings.payment.stripeMode}
                          onChange={(e) => setAdminSettings({
                            ...adminSettings,
                            payment: { ...adminSettings.payment, stripeMode: e.target.value }
                          })}
                          className="w-full border border-slate-200 dark:border-slate-800 px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 focus:bg-white transition-all"
                        >
                          <option value="test">Test / Sandbox Mode</option>
                          <option value="live">Live Production</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-1.5">Publishable Key</label>
                        <input
                          type="text"
                          value={adminSettings.payment.stripePublicKey}
                          onChange={(e) => setAdminSettings({
                            ...adminSettings,
                            payment: { ...adminSettings.payment, stripePublicKey: e.target.value }
                          })}
                          placeholder="pk_test_..."
                          className="w-full border border-slate-200 dark:border-slate-800 px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 focus:bg-white transition-all"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-1.5">Secret Key</label>
                        <input
                          type="password"
                          value={adminSettings.payment.stripeSecretKey}
                          onChange={(e) => setAdminSettings({
                            ...adminSettings,
                            payment: { ...adminSettings.payment, stripeSecretKey: e.target.value }
                          })}
                          placeholder="sk_test_..."
                          className="w-full border border-slate-200 dark:border-slate-800 px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 focus:bg-white transition-all"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* PayPal Config */}
                <div className="p-5 bg-slate-50/50 dark:bg-[#0b0f19]/30 border border-slate-200 dark:border-slate-800/80 rounded-2xl mb-6">
                  <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-slate-800/60 pb-3 mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-black text-sm">P</div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">PayPal Express Checkout</h4>
                        <p className="text-xs text-slate-400 dark:text-slate-500 font-semibold">Accept wallet transfers and PayPal bank payments.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAdminSettings({
                        ...adminSettings,
                        payment: { ...adminSettings.payment, paypalEnabled: !adminSettings.payment.paypalEnabled }
                      })}
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        adminSettings.payment.paypalEnabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-800"
                      }`}
                    >
                      <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                        adminSettings.payment.paypalEnabled ? "right-1" : "left-1"
                      }`}></span>
                    </button>
                  </div>

                  {adminSettings.payment.paypalEnabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-1.5">PayPal Mode</label>
                        <select
                          value={adminSettings.payment.paypalMode}
                          onChange={(e) => setAdminSettings({
                            ...adminSettings,
                            payment: { ...adminSettings.payment, paypalMode: e.target.value }
                          })}
                          className="w-full border border-slate-200 dark:border-slate-800 px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 focus:bg-white transition-all"
                        >
                          <option value="sandbox">Sandbox (Testing)</option>
                          <option value="live">Live / Production</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-1.5">PayPal Client ID</label>
                        <input
                          type="text"
                          value={adminSettings.payment.paypalClientId}
                          onChange={(e) => setAdminSettings({
                            ...adminSettings,
                            payment: { ...adminSettings.payment, paypalClientId: e.target.value }
                          })}
                          className="w-full border border-slate-200 dark:border-slate-800 px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 focus:bg-white transition-all"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Razorpay Config */}
                <div className="p-5 bg-slate-50/50 dark:bg-[#0b0f19]/30 border border-slate-200 dark:border-slate-800/80 rounded-2xl mb-8">
                  <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-slate-800/60 pb-3 mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center text-white font-black text-sm">R</div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Razorpay</h4>
                        <p className="text-xs text-slate-400 dark:text-slate-500 font-semibold">Enable local credit/debit, netbanking, and UPI transfers in India.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAdminSettings({
                        ...adminSettings,
                        payment: { ...adminSettings.payment, razorpayEnabled: !adminSettings.payment.razorpayEnabled }
                      })}
                      className={`w-11 h-6 rounded-full transition-all relative ${
                        adminSettings.payment.razorpayEnabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-800"
                      }`}
                    >
                      <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                        adminSettings.payment.razorpayEnabled ? "right-1" : "left-1"
                      }`}></span>
                    </button>
                  </div>

                  {adminSettings.payment.razorpayEnabled && (
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-1.5">Razorpay Key ID</label>
                      <input
                        type="text"
                        value={adminSettings.payment.razorpayKeyId}
                        onChange={(e) => setAdminSettings({
                          ...adminSettings,
                          payment: { ...adminSettings.payment, razorpayKeyId: e.target.value }
                        })}
                        placeholder="rzp_test_..."
                        className="w-full border border-slate-200 dark:border-slate-800 px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 focus:bg-white transition-all"
                      />
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-extrabold px-6 py-3 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loading size={12} /> : <FaSave />}
                    Save Checkout Settings
                  </button>
                </div>
              </form>
            )}

            {/* ADMIN TAB 5: SECURITY & POLICIES */}
            {activeTab === "security_policies" && isAdmin && (
              <form onSubmit={handleAdminSettingsSubmit}>
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
                  <span className="w-2.5 h-6 bg-slate-800 dark:bg-indigo-500 rounded-full"></span>
                  Portal Security Toggles & Session Policies
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Max Login Failures Attempt Limit</label>
                    <input
                      type="number"
                      value={adminSettings.security.maxLoginAttempts}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        security: { ...adminSettings.security, maxLoginAttempts: parseInt(e.target.value) || 5 }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1.5">Inactive Session Timeout (Minutes)</label>
                    <input
                      type="number"
                      value={adminSettings.security.sessionTimeout}
                      onChange={(e) => setAdminSettings({
                        ...adminSettings,
                        security: { ...adminSettings.security, sessionTimeout: parseInt(e.target.value) || 60 }
                      })}
                      className="w-full border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-sm font-semibold text-slate-800 dark:text-slate-100 bg-slate-50/50 dark:bg-[#0b0f19] focus:outline-none focus:border-slate-500 dark:focus:border-indigo-500 focus:bg-white dark:focus:bg-[#111827] transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-[#0b0f19] rounded-2xl mb-8 border border-slate-200/60 dark:border-slate-800/60">
                  <button
                    type="button"
                    onClick={() => setAdminSettings({
                      ...adminSettings,
                      security: { ...adminSettings.security, enforceMfa: !adminSettings.security.enforceMfa }
                    })}
                    className={`w-11 h-6 rounded-full transition-all relative shrink-0 ${
                      adminSettings.security.enforceMfa ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-800"
                    }`}
                  >
                    <span className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${
                      adminSettings.security.enforceMfa ? "right-1" : "left-1"
                    }`}></span>
                  </button>
                  <div>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100">Enforce Multi-Factor Authentication (MFA)</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">Require all dashboard administrators to register a physical authentication token or authenticator app.</p>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-extrabold px-6 py-3 rounded-xl text-xs uppercase tracking-wider shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {saving ? <Loading size={12} /> : <FaSave />}
                    Save Security Policies
                  </button>
                </div>
              </form>
            )}

            {/* ADMIN TAB 6: DIAGNOSTICS & SYSTEM PERFORMANCE */}
            {activeTab === "diagnostics" && isAdmin && (
              <div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                  <span className="w-2.5 h-6 bg-slate-800 dark:bg-indigo-500 rounded-full"></span>
                  Server Uptime & Diagnostics Logs
                </h3>

                {diagnostics ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                    {/* RAM Card */}
                    <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0b0f19]/35">
                      <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">RAM Usage</p>
                      <h4 className="text-2xl font-black text-slate-800 dark:text-white">
                        {diagnostics.totalMemory - diagnostics.freeMemory} MB <span className="text-xs text-slate-400 font-semibold">/ {diagnostics.totalMemory} MB</span>
                      </h4>
                      <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2.5 mt-3 overflow-hidden">
                        <div 
                          className="bg-indigo-500 h-2.5 rounded-full" 
                          style={{ width: `${Math.round(((diagnostics.totalMemory - diagnostics.freeMemory) / diagnostics.totalMemory) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mt-2">
                        {Math.round(((diagnostics.totalMemory - diagnostics.freeMemory) / diagnostics.totalMemory) * 100)}% Consumption Rate
                      </p>
                    </div>

                    {/* Uptime Card */}
                    <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0b0f19]/35">
                      <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">System Active Uptime</p>
                      <h4 className="text-2xl font-black text-slate-800 dark:text-white">
                        {Math.floor(diagnostics.uptime / 3600)} Hours
                      </h4>
                      <p className="text-xs text-slate-400 dark:text-slate-500 font-semibold mt-1">
                        Active connection cycles since node trigger.
                      </p>
                      <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase mt-4">
                        OS Type: {diagnostics.platform}
                      </p>
                    </div>

                    {/* Environment Card */}
                    <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0b0f19]/35">
                      <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Internal Framework</p>
                      <h4 className="text-2xl font-black text-slate-800 dark:text-white">
                        Node {diagnostics.nodeVersion}
                      </h4>
                      <p className="text-xs text-slate-400 dark:text-slate-500 font-semibold mt-1">
                        Running {diagnostics.cpuCores} active core loops.
                      </p>
                      <p className="text-[10px] font-bold text-emerald-500 uppercase mt-4 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-ping"></span> 
                        API Live Loop Online
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm font-medium mb-6">Failed to retrieve server diagnostics metrics.</p>
                )}

                {/* Operations Utility buttons */}
                <div className="p-6 bg-slate-50 dark:bg-[#0b0f19] rounded-2xl border border-slate-200/60 dark:border-slate-800/60">
                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-4 uppercase tracking-wider">Administrative Utility Actions</h4>
                  
                  <div className="flex flex-wrap gap-4">
                    <button
                      type="button"
                      disabled={purgingCache}
                      onClick={handlePurgeCache}
                      className="flex items-center gap-2 bg-rose-50 dark:bg-rose-500/10 hover:bg-rose-100 dark:hover:bg-rose-500/20 text-rose-700 dark:text-rose-450 border border-rose-200 dark:border-rose-500/20 px-5 py-3 rounded-xl text-xs font-bold transition-all active:scale-95 disabled:opacity-50"
                    >
                      <FaSyncAlt className={purgingCache ? "animate-spin" : ""} />
                      {purgingCache ? "Purging Framework Cache..." : "Purge Temp Application Caches"}
                    </button>

                    <button
                      type="button"
                      disabled={backingUp}
                      onClick={handleExportBackup}
                      className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20 px-5 py-3 rounded-xl text-xs font-bold transition-all active:scale-95 disabled:opacity-50"
                    >
                      <FaCloudUploadAlt />
                      {backingUp ? "Creating Export Blob..." : "Export settings.json Backup File"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 8: THEMES AND CUSTOMIZATION */}
            {activeTab === "themes" && (
              <div>
                <div className="flex items-center justify-between mb-6 pb-2 border-b border-slate-100 dark:border-slate-800">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                    <span className="w-2.5 h-6 bg-slate-800 dark:bg-indigo-500 rounded-full"></span>
                    Theme Options
                  </h3>
                  <span className="text-xs font-extrabold px-3 py-1 rounded-full bg-slate-100 dark:bg-[#0b0f19] text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 uppercase tracking-wider">
                    Active: {themes.find(t => t.id === currentTheme)?.name}
                  </span>
                </div>

                <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-6 leading-relaxed">
                  Personalize your dashboard interface. Choosing a theme applies instant stylistic color hues and aesthetic layouts across the dashboard environment.
                </p>

                {/* Themes Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                  {themes.map((theme) => {
                    const isActive = currentTheme === theme.id;
                    return (
                      <div
                        key={theme.id}
                        onClick={() => applyTheme(theme.id)}
                        className={`cursor-pointer rounded-2xl border-2 p-5 flex flex-col justify-between transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${
                          isActive
                            ? "border-slate-800 dark:border-indigo-500 bg-slate-50/50 dark:bg-[#0b0f19]/30 shadow-md shadow-slate-200/60 dark:shadow-none"
                            : "border-slate-200 dark:border-slate-800 bg-white dark:bg-[#161d31] hover:border-slate-350 dark:hover:border-slate-700"
                        }`}
                      >
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-base font-extrabold text-slate-800 dark:text-white tracking-tight">
                              {theme.name}
                            </span>
                            {isActive ? (
                              <span className="w-5 h-5 bg-slate-800 dark:bg-indigo-500 rounded-full flex items-center justify-center text-white shrink-0">
                                <FaCheck size={10} />
                              </span>
                            ) : (
                              <span className="w-5 h-5 rounded-full border border-slate-300 dark:border-slate-800 shrink-0"></span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-4">
                            {theme.description}
                          </p>
                        </div>

                        {/* Aesthetic Color Preview circles */}
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-6 rounded-full bg-gradient-to-tr ${theme.colorPreview} shrink-0`}></div>
                          <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Color Swatch</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Premium info alert */}
                <div className="p-4 bg-blue-50/50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 text-blue-800 dark:text-blue-400 rounded-2xl text-xs font-semibold flex gap-2.5 leading-relaxed">
                  <FaInfoCircle size={16} className="text-blue-500 shrink-0 mt-0.5" />
                  Your theme preference has been successfully written to local cache. The color highlights and background tones will automatically activate whenever you visit the dashboard.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
